import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Skeleton, SkeletonColorSchemesEnum} from '../../../../libs/shared/ui/src/components/utility/skeleton';
import {ContactDetailsSectionsEnum} from '../../../models/api/localPreferencesModel';

/*
 * Props.
 */

interface ContactDetailsSectionLoadingSkeletonProps {
  section?: ContactDetailsSectionsEnum;
  shouldRenderBottomBorder?: boolean;
  shouldRemoveSectionLoadingStyles?: boolean;
  shouldHideTitleSkeleton?: boolean;
  shouldHideContentSkeleton?: boolean;
}

interface LoadingSkeletonProps {
  $shouldRenderBottomBorder?: boolean;
  $shouldRemoveSectionLoadingStyles?: boolean;
}

/*
 * Styles.
 */

const StyledContactDetailsSectionLoadingSkeletonContainerDiv = styled.div<LoadingSkeletonProps>`
  display: flex;
  flex-flow: column;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  padding: 24px 16px 20px 16px;

  ${(p) =>
    p.$shouldRenderBottomBorder &&
    css`
      border-bottom: 1px solid ${p.theme.greys.shade30};
    `}
  ${(p) =>
    p.$shouldRemoveSectionLoadingStyles &&
    css`
      border-top: none;
      padding: 0;
    `}
`;

const StyledTitleSkeleton = styled(Skeleton)`
  margin-bottom: 8px;
`;

const StyledLineSkeleton = styled(Skeleton)`
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledLineWithTopMarginSkeleton = styled(StyledLineSkeleton)`
  margin-top: 8px;
`;

export const ContactDetailsSectionLoadingSkeleton: FC<ContactDetailsSectionLoadingSkeletonProps> = (
  props,
) => {
  const {
    section,
    shouldRemoveSectionLoadingStyles,
    shouldRenderBottomBorder,
    shouldHideTitleSkeleton,
    shouldHideContentSkeleton,
  } = props;
  switch (section) {
    case ContactDetailsSectionsEnum.NOTES:
      return (
        <StyledContactDetailsSectionLoadingSkeletonContainerDiv
          $shouldRemoveSectionLoadingStyles={shouldRemoveSectionLoadingStyles}
          $shouldRenderBottomBorder={shouldRenderBottomBorder}
        >
          {!shouldHideTitleSkeleton && <StyledTitleSkeleton height="16px" width="153px" />}
          {!shouldHideContentSkeleton && (
            <>
              <StyledLineWithTopMarginSkeleton height="130px" scheme={SkeletonColorSchemesEnum.GREY10} />
              <StyledLineWithTopMarginSkeleton height="130px" scheme={SkeletonColorSchemesEnum.GREY10} />
            </>
          )}
        </StyledContactDetailsSectionLoadingSkeletonContainerDiv>
      );
    case ContactDetailsSectionsEnum.CONVERSATIONS:
      return (
        <StyledContactDetailsSectionLoadingSkeletonContainerDiv
          $shouldRemoveSectionLoadingStyles={shouldRemoveSectionLoadingStyles}
          $shouldRenderBottomBorder={shouldRenderBottomBorder}
        >
          {!shouldHideTitleSkeleton && <StyledTitleSkeleton height="16px" width="153px" />}
          {!shouldHideContentSkeleton && (
            <>
              <StyledLineWithTopMarginSkeleton height="70px" scheme={SkeletonColorSchemesEnum.GREY10} />
              <StyledLineWithTopMarginSkeleton height="70px" scheme={SkeletonColorSchemesEnum.GREY10} />
            </>
          )}
        </StyledContactDetailsSectionLoadingSkeletonContainerDiv>
      );
    case ContactDetailsSectionsEnum.SUMMARY:
      return (
        <StyledContactDetailsSectionLoadingSkeletonContainerDiv
          $shouldRemoveSectionLoadingStyles={shouldRemoveSectionLoadingStyles}
          $shouldRenderBottomBorder={shouldRenderBottomBorder}
        >
          {!shouldHideTitleSkeleton && <StyledTitleSkeleton height="16px" width="153px" />}
          {!shouldHideContentSkeleton && (
            <>
              <StyledLineWithTopMarginSkeleton
                height="16px"
                width="120px"
                scheme={SkeletonColorSchemesEnum.GREY10}
              />
              <StyledLineSkeleton height="16px" width="238px" scheme={SkeletonColorSchemesEnum.GREY10} />
              <StyledLineWithTopMarginSkeleton
                height="16px"
                width="120px"
                scheme={SkeletonColorSchemesEnum.GREY10}
              />
              <StyledLineSkeleton height="16px" width="238px" scheme={SkeletonColorSchemesEnum.GREY10} />
              <StyledLineWithTopMarginSkeleton
                height="16px"
                width="120px"
                scheme={SkeletonColorSchemesEnum.GREY10}
              />
              <StyledLineSkeleton height="16px" width="238px" scheme={SkeletonColorSchemesEnum.GREY10} />
            </>
          )}
        </StyledContactDetailsSectionLoadingSkeletonContainerDiv>
      );
    case ContactDetailsSectionsEnum.CONTACT_INFORMATION:
    default:
      return (
        <StyledContactDetailsSectionLoadingSkeletonContainerDiv
          $shouldRemoveSectionLoadingStyles={shouldRemoveSectionLoadingStyles}
          $shouldRenderBottomBorder={shouldRenderBottomBorder}
        >
          {!shouldHideTitleSkeleton && <StyledTitleSkeleton height="16px" width="153px" />}
          {!shouldHideContentSkeleton && (
            <>
              <StyledLineWithTopMarginSkeleton
                height="16px"
                width="120px"
                scheme={SkeletonColorSchemesEnum.GREY10}
              />
              <StyledLineSkeleton height="16px" width="238px" scheme={SkeletonColorSchemesEnum.GREY10} />
              <StyledLineWithTopMarginSkeleton
                height="16px"
                width="120px"
                scheme={SkeletonColorSchemesEnum.GREY10}
              />
              <StyledLineSkeleton height="16px" width="238px" scheme={SkeletonColorSchemesEnum.GREY10} />
            </>
          )}
        </StyledContactDetailsSectionLoadingSkeletonContainerDiv>
      );
  }
};
